import useApiService from 'services/api'
import { CompanyFormShape, CompanyModal } from './CompanyModal'
import { toast } from 'react-toastify'
import React, { useState } from 'react'
import FbButton, { IconMiniButton } from '../../FbUI/FbButton'
import { PiPlus } from 'react-icons/pi'
import { IContactCompany } from '../../../models/contact_companies'
import { useQueryClient } from '@tanstack/react-query'

interface CreateDoorProxyModalProps {
  chainProxy?: IContactCompany
  unmatchedTableKey?: string
  /**
   * When true, shows only the plus icon without "Add Door" text.
   * Useful for compact footer controls in tables.
   */
  iconOnly?: boolean
}

export function CreateDoorProxyModal({
  chainProxy,
  unmatchedTableKey,
  iconOnly = false,
}: CreateDoorProxyModalProps) {
  const [show, setShow] = useState(false)
  const api = useApiService()
  const queryClient = useQueryClient()
  const { refetch } = api.useDoorProxiesOfChainProxy(chainProxy?.id)

  const apiAction = async (values: CompanyFormShape) => {
    const payload = {
      ...values,
      country: values.country || 'US',
      state: values.state || '',
      city: values.city || '',
      domain: values.domain?.toLowerCase() || '',
      name: values.name?.trim() || '',
      chain_proxy_id: chainProxy?.id,
      address: values.address || '',
      zipcode: values.zipcode || '',
    }

    try {
      const response = await api.createDoorProxy(payload)
      await refetch()

      // Invalidate the specific unmatched doors table if provided
      if (unmatchedTableKey) {
        queryClient.invalidateQueries({
          queryKey: [unmatchedTableKey],
        })
      }

      // Also invalidate the legacy pattern for backward compatibility
      if (chainProxy?.id) {
        queryClient.invalidateQueries({
          queryKey: [`UNMATCHED_DOORS_TABLE_FOR_CHAIN_PROXY_${chainProxy.id}`],
        })
      }

      toast.success('Door created successfully')
      setShow(false)
      return response
    } catch (error) {
      toast.error('Failed to create door')
      throw error
    }
  }

  return (
    <>
      {iconOnly ? (
        <IconMiniButton variant="secondary" onClick={() => setShow(true)}>
          <PiPlus size={14} />
        </IconMiniButton>
      ) : (
        <FbButton onClick={() => setShow(true)}>
          <PiPlus size={14} /> Add Door
        </FbButton>
      )}
      <CompanyModal
        defaultValues={{
          domain: chainProxy?.domain ?? '',
        }}
        hideCategoryAndType
        hideKnownDistributorsAndTags
        show={show}
        setShow={setShow}
        apiAction={apiAction}
        title="New Door"
        description="Add a company to First Bite to track additional deals and opportunities."
        acceptButtonText="Create Door"
      />
    </>
  )
}
